import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import WhatsIncluded from "@/pages/WhatsIncluded";
import HowItWorks from "@/pages/HowItWorks";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Testimonials from "@/pages/Testimonials";
import ServiceAreas from "@/pages/ServiceAreas";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Clients from "@/pages/admin/Clients";
import Bookings from "@/pages/admin/Bookings";
import Revenue from "@/pages/admin/Revenue";
import Reviews from "@/pages/admin/Reviews";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ScrollReveal() {
  const { pathname } = useLocation();
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    const targets = document.querySelectorAll("main section");
    targets.forEach((el, i) => {
      el.classList.add("reveal");
      el.style.transitionDelay = `${Math.min(i * 60, 180)}ms`;
      observer.observe(el);
    });
    return () => {
      observer.disconnect();
      targets.forEach((el) => {
        el.classList.remove("reveal", "reveal-visible");
        el.style.transitionDelay = "";
      });
    };
  }, [pathname]);
  return null;
}

function PageViewTracker() {
  const { pathname } = useLocation();
  const lastPath = useRef(null);
  useEffect(() => {
    if (typeof window.gtag === "function" && lastPath.current !== pathname) {
      lastPath.current = pathname;
      window.gtag("event", "page_view", {
        page_path: pathname,
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PageViewTracker />
        <ScrollReveal />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/whats-included" element={<WhatsIncluded />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/about" element={<About />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/service-areas" element={<ServiceAreas />} />
            <Route path="/contact" element={<Contact />} />
          </Route>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Clients />} />
            <Route path="calendar" element={<Bookings />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="reviews" element={<Reviews />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
